import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { CheckCircle, Cancel, Visibility, Download } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../../utils/api';
import { useNotification } from '../NotificationContext';

interface SupplierFormData {
  workflow?: {
    w9FilePath?: string;
    w9FileName?: string;
  };
  basicDetails: any;
  thirdPartyRisk: any;
  approvalDueDiligence: any;
  commercialDueDiligence: any;
  bids: any;
}

interface SupplierReviewFormProps {
  formData: SupplierFormData;
  formId?: string;
}

const SupplierReviewForm: React.FC<SupplierReviewFormProps> = ({ formData, formId }) => {
  const { showNotification } = useNotification();
  const [viewPdfOpen, setViewPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const renderSection = (title: string, data: any) => {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(data).map(([key, value]) => (
            <Box key={key}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </Typography>
              <Typography variant="body1">
                {(value as string) || 'Not provided'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
        Review Vendor Due Diligence Information
      </Typography>

      {/* W9 Status Section */}
      {formId && (
        <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: formData.workflow?.w9FilePath ? '#e8f5e9' : '#fff3e0' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            W9 Tax Form
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {formData.workflow?.w9FilePath ? (
              <>
                <Chip 
                  icon={<CheckCircle />} 
                  label="W9 Uploaded" 
                  color="success" 
                  variant="outlined"
                />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {formData.workflow.w9FileName || 'W9 file available'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={async () => {
                    try {
                      const response = await apiClient.get(`/api/w9/view/${formId}`, {
                        responseType: 'blob'
                      });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      setPdfUrl(url);
                      setViewPdfOpen(true);
                    } catch (error: any) {
                      console.error('Error viewing W9:', error);
                      showNotification(error.response?.data?.error || 'Failed to view W9', 'error');
                    }
                  }}
                >
                  View
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={async () => {
                    try {
                      const response = await apiClient.get(`/api/w9/download/${formId}`, {
                        responseType: 'blob'
                      });
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `W9_${formData.workflow?.w9FileName || 'document'}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      showNotification('W9 downloaded successfully', 'success');
                    } catch (error: any) {
                      console.error('Error downloading W9:', error);
                      showNotification(error.response?.data?.error || 'Failed to download W9', 'error');
                    }
                  }}
                >
                  Download
                </Button>
              </>
            ) : (
              <>
                <Chip 
                  icon={<Cancel />} 
                  label="W9 Not Uploaded" 
                  color="warning" 
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  No W9 form has been uploaded for this vendor
                </Typography>
              </>
            )}
          </Box>
        </Paper>
      )}

      {renderSection('A. Basic Details', formData.basicDetails)}
      {renderSection('B. Third Party Risk Profile', formData.thirdPartyRisk)}
      {renderSection('C. Commercial Due Diligence', formData.commercialDueDiligence)}
      {renderSection('D. Bids', formData.bids)}

      <Paper elevation={2} sx={{ p: 3, mt: 4, backgroundColor: 'success.light' }}>
        <Typography variant="body1" align="center">
          Please review all information carefully before submitting.
        </Typography>
      </Paper>

      {/* PDF Viewer Modal */}
      <Dialog
        open={viewPdfOpen}
        onClose={() => {
          setViewPdfOpen(false);
          if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          W9 Form - {formData.workflow?.w9FileName || 'Document'}
          <IconButton
            aria-label="close"
            onClick={() => {
              setViewPdfOpen(false);
              if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pdfUrl && (
            <Box sx={{ width: '100%', height: '70vh' }}>
              <iframe
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="W9 PDF Viewer"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewPdfOpen(false);
              if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierReviewForm;
